import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    useGetWhyJoinThisBySlugQuery,
    useUpdateWhyJoinThisMutation,
    useDeleteWhyJoinThisMutation,
    useCreateWhyJoinThisMutation,
} from 'api/workshopApi';
import { mediaUrl } from 'utils/mediaUrl';

export const WorkshopWhyJoinThis = () => {
    const { slug } = useParams();
    const {
        data,
        isLoading,
        isError,
        refetch,
    } = useGetWhyJoinThisBySlugQuery(slug);
    const [updateWhyJoinThis, { isLoading: isUpdating }] = useUpdateWhyJoinThisMutation();
    const [deleteWhyJoinThis, { isLoading: isDeleting }] = useDeleteWhyJoinThisMutation();
    const [createWhyJoinThis, { isLoading: isCreating }] = useCreateWhyJoinThisMutation();

    const [entries, setEntries] = useState([]);
    const [newEntry, setNewEntry] = useState({
        title: '',
        description: '',
        tagline: '',
        logo: null,
    });

    useEffect(() => {
        if (data?.data) {
            setEntries(
                data.data.map(item => ({
                    id: item.id,
                    slug: item.slug,
                    title: item.title,
                    description: item.description,
                    tagline: item.tagline,
                    logo: null,
                    existingLogoUrl: item.logo,
                }))
            );
        }
    }, [data]);

    const handleChange = (id, field, value) => {
        setEntries(prev =>
            prev.map(entry => (entry.id === id ? { ...entry, [field]: value } : entry))
        );
    };

    const handleFileChange = (id, file) => {
        setEntries(prev =>
            prev.map(entry => (entry.id === id ? { ...entry, logo: file } : entry))
        );
    };

    const handleSingleSubmit = async (e, entry) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('title', entry.title);
        formData.append('description', entry.description);
        formData.append('tagline', entry.tagline);
        if (entry.logo) {
            formData.append('logo', entry.logo);
        }

        try {
            await updateWhyJoinThis({
                slug: slug,
                topicSlug: entry.slug,
                body: formData,
            }).unwrap();
            alert(`Updated "${entry.title}" successfully!`);
            refetch();
        } catch (error) {
            console.error('Error updating:', error);
            alert(`Failed to update "${entry.title}".`);
        }
    };

    const handleDelete = async topicSlug => {
        if (!window.confirm('Are you sure you want to delete this topic?')) return;

        try {
            await deleteWhyJoinThis({ slug, topicSlug }).unwrap();
            alert('Deleted successfully!');
            refetch();
        } catch (error) {
            console.error('Delete failed:', error);
            alert('Failed to delete.');
        }
    };

    const handleNewChange = (field, value) => {
        setNewEntry(prev => ({ ...prev, [field]: value }));
    };

    const handleNewFileChange = file => {
        setNewEntry(prev => ({ ...prev, logo: file }));
    };

    const handleCreateSubmit = async e => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('title', newEntry.title);
        formData.append('description', newEntry.description);
        formData.append('tagline', newEntry.tagline);
        if (newEntry.logo) {
            formData.append('logo', newEntry.logo);
        }

        try {
            await createWhyJoinThis({ slug, formData }).unwrap();
            alert('Created successfully!');
            setNewEntry({ title: '', description: '', tagline: '', logo: null });
            refetch();
        } catch (error) {
            console.error('Create failed:', error);
            alert('Failed to create topic.');
        }
    };

    if (isLoading) return <p className="text-center mt-10">Loading...</p>;
    if (isError) return <p className="text-center mt-10 text-red-600">Error loading data.</p>;

    return (
        <section className="w-full max-w-4xl mx-auto mt-10 p-6 border rounded-lg shadow bg-white">
            <h2 className="text-2xl font-semibold mb-6 text-center">Why Join This Workshop?</h2>

            {/* Create New Entry Form */}
            <form
                onSubmit={handleCreateSubmit}
                className="border p-4 rounded-lg mb-8 space-y-4 bg-gray-50"
            >
                <h3 className="font-bold text-lg mb-2">Create New Topic</h3>

                <input
                    type="text"
                    placeholder="Title"
                    value={newEntry.title}
                    onChange={e => handleNewChange('title', e.target.value)}
                    className="border p-2 rounded w-full"
                />

                <textarea
                    placeholder="Description"
                    value={newEntry.description}
                    onChange={e => handleNewChange('description', e.target.value)}
                    className="border p-2 rounded w-full resize-none"
                    rows={4}
                />

                <input
                    type="text"
                    placeholder="Tagline"
                    value={newEntry.tagline}
                    onChange={e => handleNewChange('tagline', e.target.value)}
                    className="border p-2 rounded w-full"
                />

                <div>
                    <label className="block font-semibold mb-1">Logo</label>
                    {newEntry.logo && (
                        <img
                            src={URL.createObjectURL(newEntry.logo)}
                            alt="Preview"
                            className="h-20 mb-2 object-contain"
                        />
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={e => handleNewFileChange(e.target.files[0])}
                        className="w-full"
                    />
                </div>

                <button
                    type="submit"
                    disabled={isCreating}
                    className="bg-blue-600 text-white px-4 py-2 rounded w-full sm:w-auto disabled:opacity-50"
                >
                    {isCreating ? 'Creating...' : 'Create Topic'}
                </button>
            </form>

            {/* Existing Topics */}
            {entries.map((entry, idx) => (
                <form
                    key={entry.id}
                    onSubmit={e => handleSingleSubmit(e, entry)}
                    className="border p-4 rounded-lg mb-6 space-y-4"
                >
                    <h3 className="font-bold text-lg mb-2">Topic {idx + 1}</h3>

                    <input
                        type="text"
                        value={entry.title}
                        onChange={e => handleChange(entry.id, 'title', e.target.value)}
                        className="border p-2 rounded w-full"
                    />

                    <textarea
                        value={entry.description}
                        onChange={e => handleChange(entry.id, 'description', e.target.value)}
                        className="border p-2 rounded w-full resize-none"
                        rows={4}
                    />

                    <input
                        type="text"
                        value={entry.tagline}
                        onChange={e => handleChange(entry.id, 'tagline', e.target.value)}
                        className="border p-2 rounded w-full"
                    />

                    <div>
                        {(entry.logo || entry.existingLogoUrl) && (
                            <img
                                src={
                                    entry.logo
                                        ? URL.createObjectURL(entry.logo)
                                        : mediaUrl(entry.existingLogoUrl)
                                }
                                alt="Logo"
                                className="h-20 mb-2 object-contain"
                            />
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={e => handleFileChange(entry.id, e.target.files[0])}
                            className="w-full"
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row sm:gap-4">
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className="bg-green-600 text-white px-4 py-2 rounded mb-2 sm:mb-0 disabled:opacity-50 flex-1 text-center"
                        >
                            {isUpdating ? 'Updating...' : 'Update'}
                        </button>

                        <button
                            type="button"
                            onClick={() => handleDelete(entry.slug)}
                            disabled={isDeleting}
                            className="bg-red-600 text-white px-4 py-2 rounded disabled:opacity-50 flex-1 text-center"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                </form>
            ))}
        </section>
    );
};

export default WorkshopWhyJoinThis;
